import { useMemo, useCallback } from "react";

import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { InteractionType } from "@/routes/simulator-live-mode/types";
import {
  ExploreInteractionType,
  ExploreInteractionRow,
} from "@/v2/entities/explore/Explore.model";
import { useExploreInteractions } from "@/v2/entities/explore/Explore.api";
import { Filter, mergeFilter } from "@/v2/entities/explore/Explore.filter";
import { ExploreFiltersValue } from "@/v2/features/explore-filter-menu/ExploreFilterMenu";

import { TrainingPanelInteractionCard } from "./TrainingPanelInteractionCard";
import { InteractionListView, InteractionListItems } from "./shared";

interface InteractionViewProps {
  activeFilters: ExploreFiltersValue;
  setActiveFilters: (filters: ExploreFiltersValue) => void;
  ticketsData: any;
  isLoadingTickets: boolean;
  onNavigateToTranscript: (
    interactionId: string,
    displayId: string,
    interactionType: InteractionType
  ) => void;
  onResetNavigation: () => void;
  sharedFilter: Filter | undefined;
  selectedInteractionType: ExploreInteractionType;
  onInteractionTypeChange: (type: ExploreInteractionType) => void;
  search: string;
  onSearchChanged: (search: string) => void;
  topics?: Array<{ id: number; topic: string }>;
}

export const InteractionView = ({
  activeFilters,
  setActiveFilters,
  ticketsData,
  isLoadingTickets,
  onNavigateToTranscript,
  onResetNavigation,
  sharedFilter,
  selectedInteractionType,
  onInteractionTypeChange,
  search,
  onSearchChanged,
  topics,
}: InteractionViewProps) => {
  const effectiveInteractionType = useMemo(() => {
    return selectedInteractionType;
  }, [selectedInteractionType]);

  const searchFilter = useMemo<Filter | undefined>(() => {
    if (search) {
      const filter: Filter = {
        or: [
          { subject: { like: `%${search}%` } },
          { displayId: { like: `%${search}%` } },
          { tags: { contains: search } },
          { tenantUser: { like: `%${search}%` } },
        ],
      };

      return filter;
    }
  }, [search]);

  const effectiveFilter = useMemo<Filter | undefined>(() => {
    if (sharedFilter && searchFilter) {
      return mergeFilter(sharedFilter, searchFilter);
    }
    if (searchFilter) {
      return searchFilter;
    }
    return sharedFilter;
  }, [sharedFilter, searchFilter]);

  const {
    data: interactionsData,
    isLoading: isLoadingInteractions,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useExploreInteractions({
    source: effectiveInteractionType,
    orderBy: "createdAt",
    orderDir: "desc",
    filter: effectiveFilter,
    enabled: true,
  });

  const currentData = useMemo(() => {
    return interactionsData;
  }, [interactionsData]);

  const isLoading = useMemo(() => {
    return isLoadingInteractions;
  }, [isLoadingInteractions]);

  const handleFetchNextPage = useCallback(async () => {
    await fetchNextPage();
  }, [fetchNextPage]);

  const formattedData = useMemo(() => {
    if (!currentData) return [];

    return currentData.map((interaction) => {
      const formattedInteraction: ExploreInteractionRow = {
        ...interaction,
        id: interaction.id,
        displayId: interaction.displayId || interaction.id || "",
        subject: interaction.subject || interaction.description || "No subject",
        description:
          interaction.description || interaction.subject || "No description",
        createdAt: interaction.createdAt || new Date().toISOString(),
        updatedAt: interaction.updatedAt || new Date().toISOString(),
        status: interaction.status || "",
        channel: interaction.channel || effectiveInteractionType,
        topic: interaction.topic || "",
        category: interaction.category || "",
        countPublicMessages: interaction.countPublicMessages || 0,
        countInternalMessages: interaction.countInternalMessages || 0,
      };

      return formattedInteraction;
    });
  }, [currentData, effectiveInteractionType]);

  const handleInteractionClick = useCallback(
    (interaction: any) => {
      const interactionType =
        effectiveInteractionType === "tickets"
          ? InteractionType.TICKET
          : InteractionType.CONVERSATION;

      onNavigateToTranscript(
        interaction.id,
        interaction.displayId,
        interactionType
      );
    },
    [effectiveInteractionType, onNavigateToTranscript]
  );

  const handleTabChange = useCallback(
    (value: string) => {
      onInteractionTypeChange(value as ExploreInteractionType);
    },
    [onInteractionTypeChange]
  );

  return (
    <InteractionListView
      showTabs
      selectedTabValue={effectiveInteractionType}
      onTabChange={handleTabChange}
      activeFilters={activeFilters}
      setActiveFilters={setActiveFilters}
      filterSource={effectiveInteractionType}
      filter={effectiveFilter}
      isLoading={isLoading}
      isEmpty={formattedData?.length === 0}
      loadingText={`Loading ${effectiveInteractionType}...`}
      emptyText={`No ${effectiveInteractionType} items`}
      search={search}
      onSearchChanged={onSearchChanged}
      showSearchAndFilters={true}
      hasNextPage={hasNextPage}
      fetchNextPage={handleFetchNextPage}
      isFetchingNextPage={isFetchingNextPage}
    >
      <InteractionListItems>
        {formattedData?.map((interaction) => (
          <TrainingPanelInteractionCard
            key={interaction.id}
            interaction={interaction}
            interactionType={effectiveInteractionType}
            onClick={() => handleInteractionClick(interaction)}
          />
        ))}
      </InteractionListItems>
    </InteractionListView>
  );
};
